import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../Icon/Icon';
import SwitcherButton from '../../../../Buttons/CommonSwitcher';
import Modal from '../../../../Modal/Modal';
import ModalHeader from '../../../../Modal/ModalHeader/ModalHeader';
import ModalBody from '../../../../Modal/ModalBody/ModalBody';
import ModalFooter from '../../../../Modal/ModalFooter/ModalFooter';
import CommonButton from '../../../../Buttons/CommonButton';

const T = 'sections.campaigns.new-campaign.wizard-screens.filters';

const FilterByPoi = ({
    filterScreenByPoi,
    setFilterScreenByPoi,
    activePoiZones,
    selectedPoiZoneKeys,
    onPoiZoneKeysChange,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [localEnabled, setLocalEnabled] = useState(filterScreenByPoi);
    const [localSelectedKeys, setLocalSelectedKeys] = useState(null);

    if (!Array.isArray(activePoiZones) || activePoiZones.length === 0) return null;

    const totalCount = activePoiZones.length;

    // Count of zones currently active (source of truth is zone.isActive)
    const selectedCount = activePoiZones.filter((z) => z.isActive).length;

    const openModal = () => {
        setLocalEnabled(filterScreenByPoi);
        // Initialize from zone.isActive so inactive zones appear unchecked
        const activeKeys = new Set(
            activePoiZones.filter((z) => z.isActive).map((z) => z.key)
        );
        setLocalSelectedKeys(
            activeKeys.size === totalCount ? null : activeKeys
        );
        setIsOpen(true);
    };

    const closeModal = () => setIsOpen(false);

    const handleApply = () => {
        setFilterScreenByPoi(localEnabled);
        onPoiZoneKeysChange(localSelectedKeys);
        setIsOpen(false);
    };

    const isZoneSelected = (zone) => {
        if (localSelectedKeys === null) return true;
        return localSelectedKeys.has(zone.key);
    };

    const toggleZone = (zone) => {
        const allKeys = new Set(activePoiZones.map((z) => z.key));
        const current =
            localSelectedKeys === null ? allKeys : new Set(localSelectedKeys);

        if (current.has(zone.key)) {
            current.delete(zone.key);
        } else {
            current.add(zone.key);
        }

        // normalize: if all selected, use null
        const isAll =
            current.size === totalCount &&
            activePoiZones.every((z) => current.has(z.key));
        setLocalSelectedKeys(isAll ? null : current);
    };

    const selectAll = () => setLocalSelectedKeys(null);
    const deselectAll = () => setLocalSelectedKeys(new Set());

    const localSelectedCount =
        localSelectedKeys === null
            ? totalCount
            : activePoiZones.filter((z) => localSelectedKeys.has(z.key)).length;

    const summaryLabel = filterScreenByPoi
        ? selectedCount === totalCount
            ? t(`${T}.poi-filter-all`, 'All {{n}} zones', { n: totalCount })
            : t(`${T}.poi-filter-partial`, '{{s}} of {{t}} zones', {
                  s: selectedCount,
                  t: totalCount,
              })
        : t(`${T}.poi-filter-off-label`, 'Off');

    return (
        <div className="py-2">
            <div className="title font-light mb-2">
                {t(`${T}.poi-filter-label`, 'POI Filter')}
            </div>

            <button
                onClick={openModal}
                className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-xs border transition-colors duration-150 ${
                    filterScreenByPoi && selectedCount > 0
                        ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                        : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                }`}>
                <div className="flex items-center gap-2">
                    <Icon
                        name="map-marker-alt"
                        className="flex-shrink-0"
                    />
                    <span className="font-medium">{summaryLabel}</span>
                </div>
                <Icon
                    name="sliders-h"
                    className="opacity-50 flex-shrink-0"
                />
            </button>

            <Modal active={isOpen} toggler={closeModal} size="sm">
                <ModalHeader toggler={closeModal}>
                    {t(`${T}.poi-filter-label`, 'POI Filter')}
                </ModalHeader>

                <ModalBody>
                    {/* Enable / disable toggle */}
                    <div className="flex items-center justify-between py-3 border-b border-gray-100 mb-4">
                        <div>
                            <div className="text-sm font-medium text-gray-700">
                                {t(
                                    `${T}.poi-filter-enable`,
                                    'Filter screens by POI proximity'
                                )}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                                {t(
                                    `${T}.poi-filter-enable-hint`,
                                    'Only show screens near the selected zones'
                                )}
                            </div>
                        </div>
                        <SwitcherButton
                            status={localEnabled}
                            onToggle={() => setLocalEnabled((p) => !p)}
                            labelOn={t(`${T}.poi-filter-labelOn`, 'Active')}
                            labelOff={t(`${T}.poi-filter-labelOff`, 'Inactive')}
                        />
                    </div>

                    {/* Zone list */}
                    <div
                        className={`transition-opacity duration-150 ${
                            !localEnabled ? 'opacity-40 pointer-events-none' : ''
                        }`}>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                {t(`${T}.poi-filter-zones`, 'Active zones')}
                                <span className="ml-1.5 font-normal text-gray-400">
                                    ({localSelectedCount}/{totalCount})
                                </span>
                            </span>
                            <div className="flex items-center gap-2 text-xs">
                                <button
                                    onClick={selectAll}
                                    className="text-blue-500 hover:text-blue-700 transition-colors font-medium">
                                    {t(`${T}.poi-filter-select-all`, 'All')}
                                </button>
                                <span className="text-gray-300 select-none">|</span>
                                <button
                                    onClick={deselectAll}
                                    className="text-gray-400 hover:text-gray-600 transition-colors font-medium">
                                    {t(`${T}.poi-filter-deselect-all`, 'None')}
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5 max-h-72 overflow-y-auto pr-1">
                            {activePoiZones.map((zone) => {
                                const checked = isZoneSelected(zone);
                                return (
                                    <label
                                        key={zone.key}
                                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-colors duration-100 select-none ${
                                            checked
                                                ? 'bg-blue-50 border-blue-100 hover:bg-blue-100'
                                                : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                                        }`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleZone(zone)}
                                            className="w-4 h-4 flex-shrink-0 rounded border-gray-300 text-blue-500 focus:ring-blue-400 cursor-pointer"
                                        />
                                        <span
                                            className="w-3 h-3 rounded-full flex-shrink-0 border border-white shadow-sm"
                                            style={{ backgroundColor: zone.color }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div
                                                className="text-sm text-gray-700 font-medium truncate"
                                                title={zone.label}>
                                                {zone.label}
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                {zone.locationCount}{' '}
                                                {zone.locationCount === 1
                                                    ? t(
                                                          `${T}.poi-filter-location`,
                                                          'location'
                                                      )
                                                    : t(
                                                          `${T}.poi-filter-locations`,
                                                          'locations'
                                                      )}
                                            </div>
                                        </div>
                                        {checked && (
                                            <Icon
                                                name="check"
                                                className="text-blue-400 flex-shrink-0 text-xs"
                                            />
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </ModalBody>

                <ModalFooter>
                    <CommonButton
                        styleType="cancel"
                        onClick={closeModal}
                        className="mr-2">
                        {t('common.buttons.cancel', 'Cancel')}
                    </CommonButton>
                    <CommonButton styleType="primary" onClick={handleApply}>
                        {t('common.buttons.confirm', 'Apply')}
                    </CommonButton>
                </ModalFooter>
            </Modal>
        </div>
    );
};

FilterByPoi.propTypes = {
    filterScreenByPoi: PropTypes.bool,
    setFilterScreenByPoi: PropTypes.func,
    activePoiZones: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string,
            color: PropTypes.string,
            locationCount: PropTypes.number,
            isActive: PropTypes.bool,
        })
    ),
    selectedPoiZoneKeys: PropTypes.instanceOf(Set),
    onPoiZoneKeysChange: PropTypes.func,
};

export default FilterByPoi;
